import React, { useState, useRef, useEffect } from 'react';
import './style.scss';
import UQIcon from '../icon';

export interface UQDropdownOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectionEvents {
  event: React.MouseEvent | React.KeyboardEvent;
}

export interface OptionOnSelectData {
  value: string;
  selected: boolean;
}

export interface ActiveOptionChangeData {
  value: string;
}

export interface ComboboxBaseOpenEvents {
  event: React.MouseEvent | React.KeyboardEvent;
}

export interface ComboboxBaseOpenChangeData {
  open: boolean;
}

export type Appearance = 'outline' | 'underline' | 'filled-darker' | 'filled-lighter';
export type Size = 'small' | 'medium' | 'large';

export interface UQDropdownProps {
  options: UQDropdownOption[];
  label?: string;
  name: string;
  placeholder?: string;
  appearance?: Appearance;
  size?: Size;
  multiselect?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  required?: boolean;
  boldLabel?: boolean;
  as?: 'button';
  defaultSelectedOptions?: string[];
  selectedOptions?: string[];
  onOptionSelect?: (event: SelectionEvents, data: OptionOnSelectData) => void;
  onActiveOptionChange?: (data: ActiveOptionChangeData) => void;
  mountNode?: HTMLElement | { element?: HTMLElement | null; className?: string } | null;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (e: ComboboxBaseOpenEvents, data: ComboboxBaseOpenChangeData) => void;
  disableAutoFocus?: boolean;
  inlinePopup?: boolean;
  error?: string;
  expandIcon?: React.ReactNode;
  clearButton?: React.ReactNode;
  positioning?: any; // For advanced positioning, can be extended
}

export const UQDropdown: React.FC<UQDropdownProps> = ({
  options,
  label,
  name,
  placeholder = 'Select...',
  appearance = 'outline',
  size = 'medium',
  multiselect = false,
  clearable = false,
  disabled = false,
  required = false,
  boldLabel = false,
  as = 'button',
  defaultSelectedOptions,
  selectedOptions,
  onOptionSelect,
  onActiveOptionChange,
  mountNode,
  defaultOpen = false,
  open,
  onOpenChange,
  disableAutoFocus = false,
  inlinePopup = false,
  error,
  expandIcon,
  clearButton,
  positioning,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [activeOption, setActiveOption] = useState<string | null>(null);

  const [internalSelected, setInternalSelected] = useState<string[]>(defaultSelectedOptions ?? []);
  const isControlled = selectedOptions !== undefined;
  const selected = isControlled ? selectedOptions! : internalSelected;

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open !== undefined) setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        if (open === undefined) setIsOpen(false);
        onOpenChange?.({ event: undefined as unknown as React.MouseEvent }, { open: false });
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, open, onOpenChange]);

  const handleOpenChange = (nextOpen: boolean, event: React.MouseEvent | React.KeyboardEvent) => {
    if (onOpenChange) onOpenChange({ event }, { open: nextOpen });
    if (open === undefined) setIsOpen(nextOpen);
  };

  const handleSelect = (value: string, event: React.MouseEvent | React.KeyboardEvent) => {
    let nextSelected: string[];
    let selectedNow: boolean;
    if (multiselect) {
      if (selected.includes(value)) {
        nextSelected = selected.filter((v) => v !== value);
        selectedNow = false;
      } else {
        nextSelected = [...selected, value];
        selectedNow = true;
      }
    } else {
      nextSelected = [value];
      selectedNow = true;
      setIsOpen(false);
    }
    if (!isControlled) setInternalSelected(nextSelected);
    onOptionSelect?.({ event }, { value, selected: selectedNow });
  };

  const handleClear = () => {
    if (!isControlled) setInternalSelected([]);
    onOptionSelect?.({ event: {} as any }, { value: '', selected: false });
  };

  const handleActiveOptionChange = (value: string) => {
    setActiveOption(value);
    onActiveOptionChange?.({ value });
  };

  const renderExpandIcon = () =>
    expandIcon ?? (
      <span className="uq-dropdown-expand-icon">
        <UQIcon icon="chevron-down" />
      </span>
    );
  const renderClearButton = () =>
    clearButton ?? (
      <button
        type="button"
        className="uq-dropdown-clear"
        onClick={handleClear}
        tabIndex={-1}
        aria-label="Clear selection"
      >
        <UQIcon icon="x" />
      </button>
    );

  return (
    <div
      ref={rootRef}
      className={`uq-dropdown-root uq-dropdown-${appearance} uq-dropdown-size-${size} ${error ? 'uq-dropdown-error' : ''}`}
    >
      {label && (
        <label htmlFor={name} className={`uq-input-label${boldLabel ? ' bold' : ''}`}>
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <div className="uq-dropdown-button-wrapper">
        <button
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${name}-listbox`}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          aria-required={required}
          className="uq-dropdown-button"
          onClick={(e) => !disabled && handleOpenChange(!isOpen, e)}
          disabled={disabled}
        >
          <span className="uq-dropdown-selected-value">
            {selected.length === 0
              ? <span className="uq-dropdown-placeholder">{placeholder}</span>
              : multiselect
                ? options.filter(opt => selected.includes(opt.value)).map(opt => opt.label).join(', ')
                : options.find(opt => opt.value === selected[0])?.label}
          </span>
          {clearable && selected.length > 0 ? (
            <span
              onClick={e => {
                e.stopPropagation();
                handleClear();
              }}
              className="uq-dropdown-clear"
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <UQIcon icon="x" />
            </span>
          ) : (
            renderExpandIcon()
          )}
        </button>
        {isOpen && (
          <div
            id={`${name}-listbox`}
            role="listbox"
            className="uq-dropdown-listbox"
            tabIndex={-1}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={selected.includes(opt.value)}
                aria-disabled={opt.disabled}
                className={`uq-dropdown-option${selected.includes(opt.value) ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}`}
                tabIndex={opt.disabled ? -1 : 0}
                onClick={(e) => !opt.disabled && handleSelect(opt.value, e)}
                onMouseEnter={() => handleActiveOptionChange(opt.value)}
              >
                {multiselect && (
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    readOnly
                    tabIndex={-1}
                  />
                )}
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <p className="uq-input-error-message">{error}</p>}
    </div>
  );
};

export default UQDropdown;